import {
  type ISeriesPrimitive,
  type IPrimitivePaneView,
  type IPrimitivePaneRenderer,
  type SeriesAttachedParameter,
  type ISeriesApi,
  type SeriesType,
  type Time,
} from "lightweight-charts"

interface CanvasTarget {
  useBitmapCoordinateSpace<T>(
    f: (scope: {
      context: CanvasRenderingContext2D
      horizontalPixelRatio: number
      verticalPixelRatio: number
      bitmapSize: { width: number; height: number }
    }) => T,
  ): T
}

export interface VolumeProfileParams {
  /** Aggregated volume per price bucket */
  volumeByPrice: Map<number, number>
  maxVolume: number
  precision: number
  visible: boolean
}

/** Max width of the volume bars as a fraction of the chart canvas width */
const MAX_BAR_FRACTION = 0.15

class VolumeProfileRenderer implements IPrimitivePaneRenderer {
  constructor(
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: VolumeProfileParams,
  ) {}

  draw(target: CanvasTarget): void {
    target.useBitmapCoordinateSpace(({ context: ctx, verticalPixelRatio: vpr, bitmapSize }) => {
      const { volumeByPrice, maxVolume, precision, visible } = this._p
      if (!visible || volumeByPrice.size === 0 || maxVolume === 0) return

      const maxBarW = Math.round(bitmapSize.width * MAX_BAR_FRACTION)

      // Compute cell height from precision — one price level height in bitmap pixels
      let cellH = 4
      for (const price of volumeByPrice.keys()) {
        const y0 = this._series.priceToCoordinate(price)
        const y1 = this._series.priceToCoordinate(price + precision)
        if (y0 !== null && y1 !== null && Math.abs(y0 - y1) > 0) {
          cellH = Math.max(1, Math.round(Math.abs(y0 - y1) * vpr))
          break
        }
      }

      const maxLogVol = Math.log1p(maxVolume)

      for (const [price, vol] of volumeByPrice) {
        const y = this._series.priceToCoordinate(price)
        if (y === null) continue
        const barW = Math.round((Math.log1p(vol) / maxLogVol) * maxBarW)
        if (barW <= 0) continue
        // Anchor bars on the left edge, semi-transparent blue
        ctx.fillStyle = "rgba(80, 140, 255, 0.22)"
        ctx.fillRect(0, Math.round(y * vpr) - Math.floor(cellH / 2), barW, cellH)
      }
    })
  }
}

class VolumeProfilePaneView implements IPrimitivePaneView {
  constructor(
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: VolumeProfileParams,
  ) {}

  renderer(): IPrimitivePaneRenderer {
    return new VolumeProfileRenderer(this._series, this._p)
  }
}

export class VolumeProfilePrimitive implements ISeriesPrimitive<Time> {
  private _p: VolumeProfileParams
  private _series: ISeriesApi<SeriesType, Time> | null = null
  private _requestUpdate: (() => void) | null = null

  constructor(p: VolumeProfileParams) {
    this._p = p
  }

  attached({ series, requestUpdate }: SeriesAttachedParameter<Time>): void {
    this._series = series
    this._requestUpdate = requestUpdate
  }

  detached(): void {
    this._series = null
    this._requestUpdate = null
  }

  updateParams(params: Partial<VolumeProfileParams>): void {
    this._p = { ...this._p, ...params }
    this._requestUpdate?.()
  }

  paneViews(): readonly IPrimitivePaneView[] {
    if (!this._series) return []
    return [new VolumeProfilePaneView(this._series, this._p)]
  }
}
